import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Calendar, Phone, MessageCircle, ArrowRight, Home } from 'lucide-react';
import { BRAND_LOGO } from '../utils/constants';

const ThankYouPage = () => {
  const location = useLocation();
  const bookingData = location.state?.bookingData;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-900 via-navy-800 to-navy-900 flex items-center justify-center py-20 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-2xl w-full text-center"
      >
        {/* Logo */}
        <img 
          src={BRAND_LOGO} 
          alt="Jalsa Group" 
          className="h-24 w-auto mx-auto mb-8 drop-shadow-2xl"
        />

        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
          className="w-24 h-24 bg-gradient-to-br from-gold-400 to-gold-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-gold-400/30"
        >
          <Check className="w-12 h-12 text-navy-900" strokeWidth={3} />
        </motion.div>

        {/* Thank You Message */}
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-4xl md:text-5xl font-serif text-white mb-4"
        >
          Thank You!
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-xl text-gold-400 font-light mb-6"
        >
          Your booking request has been received
        </motion.p>

        {/* Confirmation Details */}
        {bookingData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-xl p-6 mb-8 text-left"
          >
            <h3 className="text-gold-400 font-semibold mb-4 text-center">Booking Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Property:</span>
                <p className="text-white">{bookingData.propertyName}</p>
              </div>
              <div>
                <span className="text-gray-400">Room Type:</span>
                <p className="text-white">{bookingData.roomType}</p>
              </div>
              <div>
                <span className="text-gray-400">Check-in:</span>
                <p className="text-white">{bookingData.checkIn}</p>
              </div>
              <div>
                <span className="text-gray-400">Check-out:</span>
                <p className="text-white">{bookingData.checkOut}</p>
              </div>
              <div>
                <span className="text-gray-400">Guests:</span>
                <p className="text-white">{bookingData.guests}</p>
              </div>
              <div>
                <span className="text-gray-400">Name:</span>
                <p className="text-white">{bookingData.name}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* What's Next */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-xl p-6 mb-8"
        >
          <h3 className="text-white font-semibold mb-4">What happens next?</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gold-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-navy-900 text-xs font-bold">1</span>
              </div>
              <p className="text-gray-300 text-sm">Our team will review your booking request</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gold-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-navy-900 text-xs font-bold">2</span>
              </div>
              <p className="text-gray-300 text-sm">You'll receive a confirmation call within 24 hours</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gold-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-navy-900 text-xs font-bold">3</span>
              </div>
              <p className="text-gray-300 text-sm">Complete payment to confirm your reservation</p>
            </div>
          </div>
        </motion.div>

        {/* Contact Options */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mb-8"
        >
          <p className="text-gray-400 mb-4">Need immediate assistance?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+917554229796"
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-navy-700 hover:bg-navy-600 text-white rounded-lg transition-colors duration-300"
            >
              <Phone className="w-5 h-5" />
              <span>Call Us</span>
            </a>
            <a
              href="https://wa.me/917554229796?text=Hi, I just submitted a booking request and would like to confirm."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp</span>
            </a>
          </div>
        </motion.div>

        {/* Pre-arrival Guide Offer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="bg-gradient-to-r from-gold-400/10 to-transparent border border-gold-400/30 rounded-xl p-6 mb-8"
        >
          <Calendar className="w-8 h-8 text-gold-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold mb-2">Plan Your Visit</h3>
          <p className="text-gray-400 text-sm mb-4">
            Explore Bhopal's attractions and plan your perfect stay
          </p>
          <Link
            to="/#bhopal-guide"
            className="inline-flex items-center space-x-2 text-gold-400 hover:text-gold-300 transition-colors duration-300"
          >
            <span>View Bhopal Guide</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* Back to Home */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
        >
          <Link
            to="/"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-gold-400 hover:bg-gold-500 text-navy-900 font-semibold rounded-lg transition-colors duration-300"
          >
            <Home className="w-5 h-5" />
            <span>Back to Homepage</span>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ThankYouPage;
